import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/api/client';
import type { LoginResponse, Me, UUID } from '@/api/types';
import { getSession, setSession, subscribeSession, type Session } from './session';
import { can as canWith, type Grants } from './permissions';

interface AuthContext {
  session: Session | null;
  me: Me | undefined;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResponse>;
  logout: () => Promise<void>;
  can: (code: string, assignee?: UUID | null) => boolean;
  grants: Grants | undefined;
}

const Context = createContext<AuthContext | null>(null);

export const meKey = ['auth', 'me'] as const;

export function AuthProvider({ children }: { children: ReactNode }) {
  const client = useQueryClient();
  const [session, setLocal] = useState<Session | null>(getSession);

  useEffect(
    () =>
      subscribeSession((s) => {
        setLocal(s);
        if (!s) client.clear();
        else void client.invalidateQueries({ queryKey: meKey });
      }),
    [client],
  );

  const me = useQuery({
    queryKey: meKey,
    queryFn: () => api<Me>('/auth/me'),
    enabled: !!session,
    staleTime: 5 * 60_000,
  });

  const login = useCallback(async (email: string, password: string) => {
    const result = await api<LoginResponse>('/auth/login', {
      body: { email, password },
      anonymous: true,
    });
    setSession({ token: result.token, expiresAt: result.expiresAt, user: result.user });
    return result;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
    } catch {
      // The local session ends anyway.
    }
    setSession(null);
  }, []);

  const value = useMemo<AuthContext>(() => {
    const grants = me.data?.permissions;
    return {
      session,
      me: me.data,
      loading: !!session && me.isPending,
      login,
      logout,
      grants,
      can: (code, assignee) => canWith(grants, code, assignee, me.data?.user.id),
    };
  }, [session, me.data, me.isPending, login, logout]);

  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export function useAuth(): AuthContext {
  const ctx = useContext(Context);
  if (!ctx) throw new Error('useAuth fuera de AuthProvider');
  return ctx;
}

export function useCan(code: string, assignee?: UUID | null): boolean {
  return useAuth().can(code, assignee);
}

export function Can({
  code,
  assignee,
  children,
  fallback = null,
}: {
  code: string | string[];
  assignee?: UUID | null;
  children: ReactNode;
  fallback?: ReactNode;
}) {
  const { can } = useAuth();
  const codes = Array.isArray(code) ? code : [code];
  return <>{codes.some((c) => can(c, assignee)) ? children : fallback}</>;
}
